import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Pencil, FileSpreadsheet, FileText } from "lucide-react";
import { CostingPdfPreview } from "@/components/CostingPdfPreview";
import { toast } from "sonner";
import api, { download, errMsg } from "@/lib/api";
import { money, pct, fdate, searchOpts, matchKw } from "@/lib/fmt";
import { PageHeader, StatusBadge, Empty } from "@/components/Common";
import { Autocomplete } from "@/components/Autocomplete";
import { ImportDialog } from "@/components/ImportDialog";
import { SalesFilter } from "@/components/SalesFilter";
import { Button } from "@/components/ui/button";

const KEYS = ["costing_no", "customer_name", "project_title", "sales_name"];

export default function Costings() {
  const [rows, setRows] = useState([]);
  const [kw, setKw] = useState("");
  const [sales, setSales] = useState("");
  const [vt, setVt] = useState("all");
  const [pv, setPv] = useState(null);
  const nav = useNavigate();
  const load = useCallback(() => api.get("/costings", { params: { sales_id: sales } }).then((r) => setRows(r.data)), [sales]);
  useEffect(() => { load(); }, [load]);
  const list = useMemo(() => rows.filter((r) => matchKw(r, kw, KEYS) && (vt === "all" || (r.version_type || "") === vt)), [rows, kw, vt]);

  const del = async (id) => {
    if (!window.confirm("Hapus kalkulasi ini?")) return;
    try { await api.delete(`/costings/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Kalkulasi Cost" subtitle="Hitung cost IDR/USD termasuk entertain & komisi, lalu convert menjadi penawaran.">
        <select className="h-10 rounded-md border border-input bg-white px-3 text-sm" value={vt} onChange={(e) => setVt(e.target.value)} data-testid="costings-version-filter">
          <option value="all">Semua Tipe</option><option value="partner">Partner</option><option value="user">User</option><option value="">Biasa</option>
        </select>
        <SalesFilter value={sales} onChange={setSales} />
        <ImportDialog entity="costings" title="Kalkulasi Cost" onDone={load} />
        <Button onClick={() => nav("/costings/new")} data-testid="new-costing-btn"><Plus className="h-4 w-4 mr-1" />Kalkulasi Baru</Button>
      </PageHeader>
      <div className="card-premium fade-up">
        <div className="p-5"><Autocomplete value={kw} onChange={setKw} options={searchOpts(rows, KEYS)} icon placeholder="Cari no kalkulasi, customer, project..." testId="costings-search" className="md:w-96" /></div>
        <div className="overflow-x-auto">
          <table className="tbl w-full" data-testid="costings-table">
            <thead><tr><th>No</th><th>Tanggal</th><th>Customer / Project</th><th>Sales</th><th>Mata Uang</th><th className="text-right">Total Cost</th><th className="text-right">Preview Penawaran</th><th className="text-right">Margin</th><th>Status</th><th /></tr></thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.id} className="cursor-pointer" onClick={() => (r.status === "converted" ? nav(`/quotations/${r.quotation_id}`) : nav(`/costings/${r.id}`))} data-testid={`costing-row-${i}`}>
                  <td className="font-num text-sm font-semibold text-blue-800">{r.costing_no}{!!r.rev_no && <span className="ml-1 rounded bg-rose-100 px-1.5 py-0.5 font-sans text-[10px] font-bold text-rose-700" data-testid={`costing-rev-${i}`}>Rev.{r.rev_no}</span>}{r.version_type && <span className={`ml-1 block w-fit rounded px-1.5 py-0.5 font-sans text-[10px] font-semibold uppercase ${r.version_type === "user" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`} data-testid={`costing-type-${i}`}>{r.version_type === "user" ? `User • ${r.partner_name || ""}` : "Partner"}</span>}</td>
                  <td className="text-sm">{fdate(r.created_at)}</td>
                  <td><p className="font-medium">{r.customer_name}</p><p className="text-xs text-slate-400">{r.project_title}</p></td>
                  <td className="text-sm">{r.sales_name}</td>
                  <td>{r.currency}</td>
                  <td className="text-right font-num">{money(r.total_cost, r.currency)}</td>
                  <td className="text-right font-num">{money(r.offer_total, r.currency)}</td>
                  <td className="text-right font-num">{pct(r.final_margin)}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Button size="icon" variant="ghost" title="Edit" onClick={() => nav(r.status === "converted" ? `/quotations/${r.quotation_id}?edit=1` : `/costings/${r.id}`)} data-testid={`costing-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Preview / Print PDF" onClick={() => setPv(r)} data-testid={`costing-pdf-${i}`}><FileText className="h-4 w-4 text-rose-600" /></Button>
                    <Button size="icon" variant="ghost" title="Unduh Excel" onClick={() => download(`/costings/${r.id}/xlsx`, {}, `Kalkulasi-${r.costing_no}.xlsx`.replace(/\//g, "-"))} data-testid={`costing-xlsx-${i}`}><FileSpreadsheet className="h-4 w-4 text-emerald-700" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`costing-delete-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!list.length && <Empty />}
        </div>
      </div>
      <CostingPdfPreview costing={pv} open={!!pv} onOpenChange={(o) => !o && setPv(null)} />
    </div>
  );
}
